import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Account, AccountFormData } from '../types';
import { accountsAPI, preciousMetalsAPI, cryptoAPI } from '../services/api';
import { formatCurrency, parseCurrencyInput, formatCurrencyInput } from '../utils/formatters';
import { convertCurrency } from '../services/currencyService';
import InstitutionAutocomplete from './InstitutionAutocomplete';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FormLabel } from './ui/form-label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from './ui/select';
import { Calendar } from './ui/calendar';
import { SegmentedControl } from './ui/segmented-control';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

interface AddAssetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass a non-null account to open in edit mode */
  editingAccount?: Account | null;
  /** Called after a successful save. Parent should refresh data. */
  onSuccess: () => void;
  /** Existing accounts — used to populate institution autocomplete suggestions */
  existingAccounts?: Account[];
  /** Hides balance date picker and notes textarea for a leaner onboarding experience */
  isOnboarding?: boolean;
}

const CURRENCY_OPTIONS: { code: string; flag: string; name: string }[] = [
  { code: 'CAD', flag: '🇨🇦', name: 'Canadian Dollar' },
  { code: 'USD', flag: '🇺🇸', name: 'US Dollar' },
  { code: 'EUR', flag: '🇪🇺', name: 'Euro' },
  { code: 'GBP', flag: '🇬🇧', name: 'British Pound' },
  { code: 'JPY', flag: '🇯🇵', name: 'Japanese Yen' },
  { code: 'AUD', flag: '🇦🇺', name: 'Australian Dollar' },
  { code: 'CHF', flag: '🇨🇭', name: 'Swiss Franc' },
  { code: 'CNY', flag: '🇨🇳', name: 'Chinese Yuan' },
];

function getGroupedAssetCategories(
  accountType: 'personal' | 'business'
): { label: string; options: string[] }[] {
  if (accountType === 'business') {
    return [
      {
        label: 'Banking & Investments',
        options: [
          'Business Checking',
          'Business Savings',
          'Business Money Market',
          'Business Investments',
        ],
      },
      {
        label: 'Other Assets',
        options: [
          'Business Real Estate',
          'Business Vehicles',
          'Equipment & Machinery',
          'Inventory',
          'Accounts Receivable',
          'Intellectual Property',
          'Other Business Assets',
        ],
      },
    ];
  }
  return [
    {
      label: 'Banking & Investments',
      options: [
        'Checking Account',
        'Savings Account',
        'TFSA',
        'RRSP',
        'RESP',
        'Money Market',
        'CD',
        'Brokerage Account',
      ],
    },
    {
      label: 'Other Assets',
      options: [
        'Real Estate',
        'Vehicles',
        'Precious Metals',
        'Cryptocurrency',
        'Personal Property',
        'Other Assets',
      ],
    },
  ];
}

function shouldShowInstitution(category: string) {
  return !['Real Estate', 'Precious Metals', 'Vehicles', 'Cryptocurrency'].includes(category);
}

function getInstitutionLabel(category: string) {
  switch (category) {
    case 'Real Estate': return 'Property Type';
    case 'Precious Metals': return 'Storage Location';
    case 'Vehicles': return 'Vehicle Type';
    case 'Cryptocurrency': return 'Exchange/Wallet';
    default: return 'Institution';
  }
}

function getInstitutionPlaceholder(category: string) {
  switch (category) {
    case 'Real Estate': return 'e.g., Primary Residence, Rental Property, etc.';
    case 'Precious Metals': return 'e.g., Home Safe, Bank Vault, etc.';
    case 'Vehicles': return 'e.g., Car, Truck, Boat, etc.';
    case 'Cryptocurrency': return 'e.g., Coinbase, Binance, Hardware wallet, etc.';
    default: return 'e.g., Chase Bank, Vanguard, etc.';
  }
}

// Returns undefined for empty or non-numeric input so validation rejects it
// rather than silently saving 0 (parseCurrencyInput returns 0 for "asdf" etc.)
const parseValueInput = (raw: string): number | undefined => {
  if (raw.trim() === '' || !/\d/.test(raw)) return undefined;
  return parseCurrencyInput(raw);
};

const EMPTY_FORM: AccountFormData = {
  type: 'asset',
  account_type: 'personal',
  institution: '',
  name: '',
  category: '',
  currency: 'CAD',
  value: undefined,
  date: new Date().toISOString().split('T')[0],
  notes: '',
};

const AddAssetModal: React.FC<AddAssetModalProps> = ({
  open,
  onOpenChange,
  editingAccount = null,
  onSuccess,
  existingAccounts = [],
  isOnboarding = false,
}) => {
  const [formData, setFormData] = useState<AccountFormData>(EMPTY_FORM);
  const [currencyInputValue, setCurrencyInputValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ category?: string; name?: string; value?: string }>({});
  const [touched, setTouched] = useState<{ category?: boolean; name?: boolean; value?: boolean }>({});
  const [submitting, setSubmitting] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Balance date picker
  const [balanceDatePickerOpen, setBalanceDatePickerOpen] = useState(false);
  const balanceDateTriggerRef = useRef<HTMLButtonElement>(null);
  const balanceDateCalendarRef = useRef<HTMLDivElement>(null);

  // Precious metals
  const [preciousMetalData, setPreciousMetalData] = useState({
    metalType: 'gold' as 'gold' | 'silver' | 'platinum' | 'palladium',
    weight: 0,
    weightUnit: 'oz' as 'oz' | 'g',
    storageLocation: 'home' as 'home' | 'vault' | 'other',
  });
  const [metalValue, setMetalValue] = useState<number | null>(null);
  const [metalValueLoading, setMetalValueLoading] = useState(false);
  const [metalValueSource, setMetalValueSource] = useState('');
  const [metalLoadingStep, setMetalLoadingStep] = useState('Fetching daily prices');

  // Crypto
  const [cryptoData, setCryptoData] = useState({
    coinType: '',
    quantity: 0,
    walletAddress: '',
    exchange: '',
  });
  const [cryptoValue, setCryptoValue] = useState<number | null>(null);
  const [cryptoValueLoading, setCryptoValueLoading] = useState(false);
  const [cryptoValueSource, setCryptoValueSource] = useState('');
  const [cryptoLoadingStep, setCryptoLoadingStep] = useState('Fetching daily prices');

  // Cached daily prices (loaded lazily when Precious Metals or Crypto selected)
  const [cachedMetalPrices, setCachedMetalPrices] = useState<any[]>([]);
  const [cachedCryptoPrices, setCachedCryptoPrices] = useState<any[]>([]);

  // ── Close date picker when modal closes ──────────────────────────────────
  useEffect(() => {
    if (!open) setBalanceDatePickerOpen(false);
  }, [open]);

  // ── Close date picker on outside click ──────────────────────────────────
  useEffect(() => {
    if (!balanceDatePickerOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !balanceDateTriggerRef.current?.contains(target) &&
        !balanceDateCalendarRef.current?.contains(target)
      ) {
        setBalanceDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [balanceDatePickerOpen]);

  // ── Initialize form when modal opens ────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (editingAccount) {
      setFormData({
        type: editingAccount.type,
        account_type: editingAccount.account_type,
        institution: editingAccount.institution,
        name: editingAccount.name,
        category: editingAccount.category,
        currency: editingAccount.currency,
        value: editingAccount.current_value,
        date: editingAccount.latest_snapshot_date || new Date().toISOString().split('T')[0],
        notes: editingAccount.notes || '',
      });
      setCurrencyInputValue(
        editingAccount.current_value ? formatCurrencyInput(editingAccount.current_value) : ''
      );
      setFormDirty(false);
    } else {
      resetForm();
    }
  }, [open, editingAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormDirty(false);
    setFormData({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] });
    setCurrencyInputValue('');
    setFieldErrors({});
    setTouched({});
    setSubmitting(false);
    setPreciousMetalData({ metalType: 'gold', weight: 0, weightUnit: 'oz', storageLocation: 'home' });
    setMetalValue(null);
    setMetalValueLoading(false);
    setMetalValueSource('');
    setMetalLoadingStep('Fetching current prices');
    setCryptoData({ coinType: '', quantity: 0, walletAddress: '', exchange: '' });
    setCryptoValue(null);
    setCryptoValueLoading(false);
    setCryptoValueSource('');
    setCryptoLoadingStep('Fetching current prices');
  };

  const handleClose = () => {
    if (formDirty) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  // ── Price fetching ───────────────────────────────────────────────────────
  const fetchDailyPrices = async () => {
    try {
      const [metalPrices, cryptoPrices] = await Promise.all([
        preciousMetalsAPI.getCurrentPricesPublic(),
        cryptoAPI.getCoinPrices(),
      ]);
      setCachedMetalPrices(metalPrices);
      setCachedCryptoPrices(cryptoPrices);
    } catch {
      setCachedMetalPrices([]);
      setCachedCryptoPrices([]);
    }
  };

  const fetchPricesIfNeeded = async (category: string) => {
    if (
      (category === 'Precious Metals' || category === 'Cryptocurrency') &&
      cachedMetalPrices.length === 0 &&
      cachedCryptoPrices.length === 0
    ) {
      await fetchDailyPrices();
    }
  };

  // ── Metal value calculation ──────────────────────────────────────────────
  const calculateMetalValue = useCallback(async () => {
    if (formData.category !== 'Precious Metals') {
      setMetalValue(null);
      setMetalValueSource('');
      setMetalValueLoading(false);
      return;
    }
    if (!preciousMetalData.metalType || !preciousMetalData.weight) {
      setMetalValue(null);
      setMetalValueSource('');
      setMetalValueLoading(false);
      return;
    }
    try {
      setMetalValueLoading(true);
      setMetalLoadingStep('Calculating value using daily price');
      const metalPrice = cachedMetalPrices.find((p: any) => p.metal_type === preciousMetalData.metalType);
      if (metalPrice) {
        let weightInOz = preciousMetalData.weight;
        if (preciousMetalData.weightUnit === 'g') weightInOz = preciousMetalData.weight / 31.1035;
        let pricePerOz: number;
        if (formData.currency === 'USD') {
          pricePerOz = metalPrice.price_usd;
        } else if (formData.currency === 'CAD') {
          pricePerOz = metalPrice.price_cad;
        } else {
          pricePerOz = await convertCurrency(metalPrice.price_usd, 'USD', formData.currency);
        }
        setMetalValue(weightInOz * pricePerOz);
        setMetalValueSource(metalPrice.source);
      } else {
        setMetalValue(null);
        setMetalValueSource('');
      }
    } catch {
      setMetalValue(null);
    } finally {
      setMetalValueLoading(false);
      setMetalLoadingStep('Fetching daily prices');
    }
  }, [formData.category, preciousMetalData.weight, preciousMetalData.metalType, preciousMetalData.weightUnit, formData.currency, cachedMetalPrices]);

  useEffect(() => {
    if (formData.category === 'Precious Metals' && preciousMetalData.metalType && preciousMetalData.weight > 0) {
      calculateMetalValue();
    } else {
      setMetalValue(null);
      setMetalValueSource('');
      setMetalValueLoading(false);
    }
  }, [calculateMetalValue, formData.category, formData.currency, preciousMetalData.metalType, preciousMetalData.weight]);

  // ── Crypto value calculation ─────────────────────────────────────────────
  const calculateCryptoValue = useCallback(async () => {
    if (formData.category !== 'Cryptocurrency') {
      setCryptoValue(null);
      setCryptoValueSource('');
      setCryptoValueLoading(false);
      return;
    }
    if (!cryptoData.coinType || !cryptoData.quantity) {
      setCryptoValue(null);
      setCryptoValueSource('');
      setCryptoValueLoading(false);
      return;
    }
    try {
      setCryptoValueLoading(true);
      setCryptoLoadingStep('Calculating value using daily price');
      const coinPrice = cachedCryptoPrices.find((p: any) => p.coin_type === cryptoData.coinType);
      if (!coinPrice) {
        setCryptoValue(null);
        setCryptoValueSource('No price data available');
        return;
      }
      const value = formData.currency === 'CAD'
        ? coinPrice.price_cad * cryptoData.quantity
        : coinPrice.price_usd * cryptoData.quantity;
      setCryptoValue(value);
      setCryptoValueSource(coinPrice.source);
    } catch {
      setCryptoValue(null);
      setCryptoValueSource('Error fetching price data');
    } finally {
      setCryptoValueLoading(false);
      setCryptoLoadingStep('Fetching daily prices');
    }
  }, [cryptoData, formData.currency, formData.category, cachedCryptoPrices]);

  useEffect(() => {
    if (formData.category === 'Cryptocurrency' && cryptoData.coinType && cryptoData.quantity > 0) {
      calculateCryptoValue();
    } else {
      setCryptoValue(null);
      setCryptoValueSource('');
      setCryptoValueLoading(false);
    }
  }, [calculateCryptoValue, formData.category, cryptoData.coinType, cryptoData.quantity]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const errors: { category?: string; name?: string; value?: string } = {};
    if (!String(formData.category ?? '').trim()) errors.category = 'Asset category is required.';
    if (!String(formData.name ?? '').trim()) errors.name = 'Account name is required.';
    const requireBalance =
      !editingAccount &&
      formData.category !== 'Precious Metals' &&
      formData.category !== 'Cryptocurrency';
    if (requireBalance && formData.value == null) errors.value = 'Balance amount is required.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      if (editingAccount) {
        await accountsAPI.updateAccount(editingAccount.id, {
          type: formData.type,
          account_type: formData.account_type,
          institution: formData.institution,
          name: formData.name,
          category: formData.category,
          currency: formData.currency,
          notes: formData.notes,
        });
      } else if (formData.category === 'Cryptocurrency') {
        await cryptoAPI.createCryptoAccount({
          account_type: formData.account_type,
          category: formData.category,
          name: formData.name,
          currency: formData.currency,
          coinType: cryptoData.coinType,
          quantity: cryptoData.quantity,
          exchange: cryptoData.exchange,
          walletAddress: cryptoData.walletAddress,
          notes: formData.notes,
        });
      } else {
        const accountResult = await accountsAPI.addAccount(formData);
        if (formData.category === 'Precious Metals') {
          await preciousMetalsAPI.createMetal({
            accountId: accountResult.id,
            metalData: {
              metal_type: preciousMetalData.metalType,
              weight: preciousMetalData.weight,
              weight_unit: preciousMetalData.weightUnit,
              storage_location: preciousMetalData.storageLocation,
              storage_details: formData.institution || undefined,
            },
          });
        }
      }

      onOpenChange(false);
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error saving account:', error);
      let msg = 'Failed to save account. Please try again.';
      if (formData.category === 'Cryptocurrency') msg = 'Failed to create cryptocurrency account. Please try again.';
      else if (formData.category === 'Precious Metals') msg = 'Failed to create precious metals account. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const existingInstitutions = existingAccounts.map((a) => a.institution).filter(Boolean) as string[];

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-dialog-content border border-border shadow-2xl">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} noValidate className="space-y-4 add-asset-form">
            {/* Personal / Business toggle */}
            <div className="flex flex-col gap-2">
              <FormLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</FormLabel>
              <SegmentedControl
                aria-label="Account type"
                value={formData.account_type}
                onValueChange={(v) => {
                  setFormDirty(true);
                  setFormData({ ...formData, account_type: v, category: '' });
                }}
                options={[
                  { value: 'personal', label: 'Personal', icon: '👤' },
                  { value: 'business', label: 'Business', icon: '🏢' },
                ]}
              />
            </div>

            {/* Asset category */}
            <div className="space-y-2">
              <FormLabel htmlFor="category">Asset Category</FormLabel>
              <Select
                value={formData.category}
                onOpenChange={(o) => { if (o) setTouched((prev) => ({ ...prev, category: true })); }}
                onValueChange={async (val) => {
                  setFormDirty(true);
                  setTouched((prev) => ({ ...prev, category: true }));
                  if (String(val).trim()) setFieldErrors((prev) => ({ ...prev, category: undefined }));
                  setFormData({ ...formData, category: val });
                  await fetchPricesIfNeeded(val);
                }}
              >
                <SelectTrigger
                  id="category"
                  className={cn('w-full', fieldErrors.category && 'border-red-400')}
                  onBlur={() => {
                    if (!touched.category) return;
                    if (!String(formData.category ?? '').trim()) {
                      setFieldErrors((prev) => ({ ...prev, category: 'Asset category is required.' }));
                    }
                  }}
                >
                  <SelectValue placeholder="Select an asset category" />
                </SelectTrigger>
                <SelectContent>
                  {getGroupedAssetCategories(formData.account_type).map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.options.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.category && <p className="text-xs text-red-400">{fieldErrors.category}</p>}
            </div>

            {/* Institution (conditional) */}
            {shouldShowInstitution(formData.category) && (
              <div className="space-y-2">
                <FormLabel htmlFor="institution" optional>{getInstitutionLabel(formData.category)}</FormLabel>
                <InstitutionAutocomplete
                  value={formData.institution}
                  onChange={(value) => { setFormDirty(true); setFormData({ ...formData, institution: value }); }}
                  existingInstitutions={existingInstitutions}
                  placeholder={getInstitutionPlaceholder(formData.category)}
                />
              </div>
            )}

            {/* Account name */}
            <div className="space-y-2">
              <FormLabel htmlFor="asset-account-name">
                {formData.category === 'Precious Metals' ? 'Metal Description' :
                 formData.category === 'Real Estate' ? 'Property Name' :
                 formData.category === 'Vehicles' ? 'Vehicle Name' :
                 formData.category === 'Personal Property' ? 'Item Name' :
                 formData.category === 'Business Real Estate' ? 'Property Name' :
                 formData.category === 'Business Vehicles' ? 'Vehicle Name' :
                 formData.category === 'Cryptocurrency' ? 'Coin Description' :
                 'Account Name'}
              </FormLabel>
              <Input
                id="asset-account-name"
                name="accountLabel"
                autoComplete="off"
                value={formData.name}
                onChange={(e) => {
                  setTouched((prev) => ({ ...prev, name: true }));
                  const val = e.target.value;
                  if (String(val).trim()) setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  setFormDirty(true);
                  setFormData({ ...formData, name: val });
                }}
                onBlur={(e) => {
                  if (!touched.name) return;
                  if (!String(e.target.value).trim()) {
                    setFieldErrors((prev) => ({ ...prev, name: 'Account name is required.' }));
                  }
                }}
                placeholder={
                  formData.category === 'Precious Metals' ? 'e.g., Gold Bars, Silver Coins, etc.' :
                  formData.category === 'Real Estate' ? 'e.g., Primary Residence, Rental Property, etc.' :
                  formData.category === 'Vehicles' ? 'e.g., 2020 Toyota Camry, 2018 Ford F-150, etc.' :
                  formData.category === 'Personal Property' ? 'e.g., Art Collection, Jewelry, etc.' :
                  formData.category === 'Business Real Estate' ? 'e.g., Office Building, Warehouse, etc.' :
                  formData.category === 'Business Vehicles' ? 'e.g., Delivery Van, Company Car, etc.' :
                  formData.category === 'Cryptocurrency' ? 'e.g., My Bitcoin, My Ethereum, etc.' :
                  'e.g., Checking Account, 401(k), etc.'
                }
                className={cn('bg-[var(--background-dark)]', fieldErrors.name && 'border-red-400')}
              />
              {fieldErrors.name && <p className="text-xs text-red-400">{fieldErrors.name}</p>}
            </div>

            {/* Precious metals fields */}
            {formData.category === 'Precious Metals' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <FormLabel htmlFor="metalType">Metal Type</FormLabel>
                    <Select
                      value={preciousMetalData.metalType}
                      onValueChange={(val) =>
                        setPreciousMetalData({ ...preciousMetalData, metalType: val as 'gold' | 'silver' | 'platinum' | 'palladium' })
                      }
                    >
                      <SelectTrigger id="metalType"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="platinum">Platinum</SelectItem>
                        <SelectItem value="palladium">Palladium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <FormLabel htmlFor="storageLocation">Storage Location</FormLabel>
                    <Select
                      value={preciousMetalData.storageLocation}
                      onValueChange={(val) =>
                        setPreciousMetalData({ ...preciousMetalData, storageLocation: val as 'home' | 'vault' | 'other' })
                      }
                    >
                      <SelectTrigger id="storageLocation"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="home">Home</SelectItem>
                        <SelectItem value="vault">Bank Vault</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <FormLabel htmlFor="weight">Weight</FormLabel>
                    <Input
                      type="number"
                      id="weight"
                      value={preciousMetalData.weight || ''}
                      onChange={(e) =>
                        setPreciousMetalData({ ...preciousMetalData, weight: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="0.00"
                      step="0.01"
                      required
                      className="bg-[var(--background-dark)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <FormLabel htmlFor="weightUnit">Unit</FormLabel>
                    <Select
                      value={preciousMetalData.weightUnit}
                      onValueChange={(val) =>
                        setPreciousMetalData({ ...preciousMetalData, weightUnit: val as 'oz' | 'g' })
                      }
                    >
                      <SelectTrigger id="weightUnit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oz">Ounces (oz)</SelectItem>
                        <SelectItem value="g">Grams (g)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <FormLabel>Estimated Current Value</FormLabel>
                  <div className="metal-value-display">
                    {metalValueLoading ? (
                      <div className="metal-value-container">
                        <span className="loading-text">
                          <div className="loading-spinner" />
                          {metalLoadingStep}<span className="loading-dots" />
                        </span>
                      </div>
                    ) : metalValue !== null ? (
                      <div className="metal-value-container">
                        <span className="metal-value">{formatCurrency(metalValue, formData.currency)}</span>
                        <span className="metal-value-source">
                          {metalValueSource.includes('estimated (offline)') ? (
                            <span title="Live price data unavailable. Using estimated prices.">
                              ⚠️ estimated prices (offline)
                            </span>
                          ) : (
                            <span title={`Price data from ${metalValueSource}`}>
                              📊 {metalValueSource}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : preciousMetalData.metalType && preciousMetalData.weight > 0 ? (
                      <span className="error-text">Unable to calculate value</span>
                    ) : (
                      <span className="info-text">Enter metal type and weight to see estimated value</span>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Crypto fields */}
            {formData.category === 'Cryptocurrency' && (
              <>
                <div className="space-y-2">
                  <FormLabel htmlFor="coinType">Coin Type</FormLabel>
                  <Select
                    value={cryptoData.coinType || undefined}
                    onValueChange={(val) => setCryptoData({ ...cryptoData, coinType: val })}
                  >
                    <SelectTrigger id="coinType"><SelectValue placeholder="Select coin type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bitcoin">Bitcoin (BTC)</SelectItem>
                      <SelectItem value="ethereum">Ethereum (ETH)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="quantity">Quantity</FormLabel>
                  <Input
                    type="number"
                    id="quantity"
                    value={cryptoData.quantity || ''}
                    onChange={(e) => setCryptoData({ ...cryptoData, quantity: parseFloat(e.target.value) || 0 })}
                    step="0.00000001"
                    min={0}
                    placeholder="e.g., 0.5"
                    required
                    className="bg-[var(--background-dark)]"
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="exchange">Exchange/Wallet</FormLabel>
                  <Input
                    id="exchange"
                    value={cryptoData.exchange}
                    onChange={(e) => setCryptoData({ ...cryptoData, exchange: e.target.value })}
                    placeholder="e.g., Coinbase, Binance, Hardware wallet, etc."
                    required
                    className="bg-[var(--background-dark)]"
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="walletAddress">Wallet Address (Optional)</FormLabel>
                  <Input
                    id="walletAddress"
                    value={cryptoData.walletAddress}
                    onChange={(e) => setCryptoData({ ...cryptoData, walletAddress: e.target.value })}
                    placeholder="e.g., 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                    className="bg-[var(--background-dark)]"
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel>Estimated Current Value</FormLabel>
                  <div className="metal-value-display">
                    {cryptoValueLoading ? (
                      <div className="metal-value-container">
                        <span className="loading-text">
                          <div className="loading-spinner" />
                          {cryptoLoadingStep}<span className="loading-dots" />
                        </span>
                      </div>
                    ) : cryptoValue !== null ? (
                      <div className="metal-value-container">
                        <span className="metal-value">{formatCurrency(cryptoValue, formData.currency)}</span>
                        <span className="metal-value-source">{cryptoValueSource}</span>
                      </div>
                    ) : cryptoData.coinType && cryptoData.quantity > 0 ? (
                      <span className="error-text">Unable to calculate value</span>
                    ) : (
                      <span className="info-text">Enter coin type and quantity to see estimated value</span>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Currency */}
            <div className="space-y-2">
              <FormLabel htmlFor="currency">Currency</FormLabel>
              <Select
                value={formData.currency}
                onValueChange={(val) => setFormData({ ...formData, currency: val })}
              >
                <SelectTrigger id="currency"><SelectValue placeholder="Select currency..." /></SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((cur) => (
                    <SelectItem key={cur.code} value={cur.code}>
                      <span className="flex items-center gap-2">
                        <span>{cur.flag}</span>
                        <span className="font-medium">{cur.code}</span>
                        <span className="text-muted-foreground font-normal ml-2">{cur.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Balance date + value — hidden in onboarding mode */}
            {!isOnboarding && !editingAccount &&
              formData.category !== 'Precious Metals' &&
              formData.category !== 'Cryptocurrency' && (
              <>
                <div className="space-y-2 relative">
                  <FormLabel htmlFor="balance-date-trigger">Balance Date</FormLabel>
                  <Button
                    ref={balanceDateTriggerRef}
                    type="button"
                    id="balance-date-trigger"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal bg-[var(--background-dark)] hover:bg-[var(--background-dark)] gap-2',
                      formData.date
                        ? 'text-foreground hover:text-foreground'
                        : 'text-muted-foreground/80 hover:text-muted-foreground/80'
                    )}
                    onClick={() => setBalanceDatePickerOpen((o) => !o)}
                    aria-expanded={balanceDatePickerOpen}
                  >
                    <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    {formData.date ? format(parseISO(formData.date), 'MMM d, yyyy') : 'Pick a date'}
                  </Button>
                  {balanceDatePickerOpen && (
                    <div
                      ref={balanceDateCalendarRef}
                      className="balance-date-inline absolute left-0 top-full z-50 w-fit rounded-md border border-input bg-[var(--background-dark)] p-2"
                    >
                      <Calendar
                        mode="single"
                        selected={formData.date ? parseISO(formData.date) : undefined}
                        onSelect={(selected) => {
                          if (selected) {
                            setFormData({ ...formData, date: format(selected, 'yyyy-MM-dd') });
                            setBalanceDatePickerOpen(false);
                            requestAnimationFrame(() => {
                              const next = document.getElementById('value') as HTMLInputElement | null;
                              next?.focus();
                            });
                          }
                        }}
                        defaultMonth={formData.date ? parseISO(formData.date) : new Date()}
                        disabled={(date) => date > new Date()}
                        fixedWeeks
                        captionLayout="dropdown"
                        startMonth={new Date(new Date().getFullYear() - 10, 0)}
                        endMonth={new Date()}
                        hideNavigation
                        className="p-2 text-sm bg-[var(--background-dark)]"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <FormLabel htmlFor="value">
                    Balance on {formData.date ? format(parseISO(formData.date), 'MMM d, yyyy') : 'selected date'}
                  </FormLabel>
                  <Input
                    type="text"
                    id="value"
                    value={currencyInputValue}
                    onChange={(e) => {
                      setFormDirty(true);
                      setTouched((prev) => ({ ...prev, value: true }));
                      const raw = e.target.value;
                      setCurrencyInputValue(raw);
                      const newValue = parseValueInput(raw);
                      setFormData({ ...formData, value: newValue });
                      if (newValue != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const newValue = parseValueInput(raw);
                      setFormData({ ...formData, value: newValue });
                      setCurrencyInputValue(newValue != null ? formatCurrencyInput(newValue) : '');
                      if (!touched.value) return;
                      if (newValue == null) {
                        setFieldErrors((prev) => ({ ...prev, value: 'Balance amount is required.' }));
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      setFormDirty(true);
                      const text = e.clipboardData.getData('text/plain').trim();
                      const parsed = parseValueInput(text);
                      setFormData((prev) => ({ ...prev, value: parsed }));
                      setCurrencyInputValue(text === '' ? '' : (parsed != null ? formatCurrencyInput(parsed) : ''));
                      if (parsed != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                    }}
                    placeholder="e.g., $1,234.56 or 1234.56"
                    className={cn('bg-[var(--background-dark)]', fieldErrors.value && 'border-red-400')}
                  />
                  {fieldErrors.value && <p className="text-xs text-red-400">{fieldErrors.value}</p>}
                </div>
              </>
            )}

            {/* Onboarding: simplified balance input (no date picker) */}
            {isOnboarding && !editingAccount &&
              formData.category !== 'Precious Metals' &&
              formData.category !== 'Cryptocurrency' && (
              <div className="space-y-2">
                <FormLabel htmlFor="value">Current Value</FormLabel>
                <Input
                  type="text"
                  id="value"
                  value={currencyInputValue}
                  onChange={(e) => {
                    setFormDirty(true);
                    setTouched((prev) => ({ ...prev, value: true }));
                    const raw = e.target.value;
                    setCurrencyInputValue(raw);
                    const newValue = parseValueInput(raw);
                    setFormData({ ...formData, value: newValue });
                    if (newValue != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const newValue = parseValueInput(raw);
                    setFormData({ ...formData, value: newValue });
                    setCurrencyInputValue(raw.trim() === '' ? '' : formatCurrencyInput(newValue as number));
                    if (!touched.value) return;
                    if (newValue == null) {
                      setFieldErrors((prev) => ({ ...prev, value: 'Balance amount is required.' }));
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    setFormDirty(true);
                    const text = e.clipboardData.getData('text/plain').trim();
                    const parsed = parseValueInput(text);
                    setFormData((prev) => ({ ...prev, value: parsed }));
                    setCurrencyInputValue(text === '' ? '' : (parsed != null ? formatCurrencyInput(parsed) : ''));
                    if (parsed != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                  }}
                  placeholder="e.g., $1,234.56 or 1234.56"
                  className={cn('bg-[var(--background-dark)]', fieldErrors.value && 'border-red-400')}
                />
                {fieldErrors.value && <p className="text-xs text-red-400">{fieldErrors.value}</p>}
              </div>
            )}

            {/* Notes — hidden in onboarding */}
            {!isOnboarding && (
              <div className="space-y-2">
                <FormLabel htmlFor="notes" optional>Notes</FormLabel>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => { setFormDirty(true); setFormData({ ...formData, notes: e.target.value }); }}
                  placeholder="Optional notes about this account"
                  rows={3}
                  className="bg-[var(--background-dark)]"
                />
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <span className="loading-text flex items-center gap-2">
                    <span className="loading-spinner" />
                    {editingAccount ? 'Updating...' :
                     formData.category === 'Cryptocurrency' ? 'Creating Crypto Account...' :
                     formData.category === 'Precious Metals' ? 'Creating Metal Account...' :
                     'Adding...'}
                  </span>
                ) : (
                  editingAccount ? 'Update Asset' : 'Add Asset'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Discard confirmation */}
      <Dialog open={showDiscardConfirm} onOpenChange={(o) => { if (!o) setShowDiscardConfirm(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Discard changes?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">You have unsaved changes. Discard them and close the form?</p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="secondary" onClick={() => setShowDiscardConfirm(false)}>
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setShowDiscardConfirm(false);
                onOpenChange(false);
                resetForm();
              }}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddAssetModal;
